import { mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, BlastBarrel, Hero } from '../types.ts';
import { damageEnemy, damageHero, ev } from './combat.ts';

/**
 * The blasting (La Quemadura 4, docs/QUEMADURA.md): the first Vigil's watchtower stands where an extraction's node
 * would, with Tobin shut in it; the nightmares come out of cracks in the glass around it. Powder barrels wait in a
 * stack by the tower (Tobin hands one down every so often): a hero who stays by one lifts it, and walking it to a
 * crack plants it there with a short fuse; enough blasts seal the crack. A barrel takes the blows its carrier takes, and enough of them set it off in the hands.
 * Every crack sealed, the beacon on the tower is lit, and that wins it. The tower falling loses it.
 */

const carried = (s: BattleState, h: Hero): BlastBarrel | undefined => s.blast?.barrels.find((b) => b.pid === h.pid);

export const blastTick = (s: BattleState): void => {
  const r = s.rules?.blast;
  if (!r) return;
  const st = (s.blast ??= { barrels: [], sealed: r.cracks.map(() => false), blasts: r.cracks.map(() => 0), nextId: 1, lit: 0, next: r.first });
  if (!s.started || st.lit) return;
  // Tobin hands a barrel down from the tower now and then, one waiting on the stack at most.
  const waiting = st.barrels.some((b) => !b.pid && b.crack < 0 && withinRange(b.x, b.y, r.stack.x, r.stack.y, r.reach));
  if (waiting) st.next = s.waveT + r.refill;
  else if (s.waveT >= st.next) {
    st.barrels.push({ id: st.nextId++, x: r.stack.x, y: r.stack.y, hp: r.hp, pid: '', held: 0, fuse: 0, crack: -1 });
    ev(s, { t: 'blast', kind: 'ready', x: r.stack.x, y: r.stack.y });
  }
  const standing = s.heroes.filter((h) => h.downTicks === 0);
  const keep: BlastBarrel[] = [];
  for (const b of st.barrels) {
    // Set off in the hands (or on the ground) by the blows it took.
    if (b.hp <= 0) {
      boom(s, b, false);
      continue;
    }
    if (b.fuse) {
      if (s.tick < b.fuse) {
        keep.push(b);
        continue;
      }
      boom(s, b, true);
      continue;
    }
    const h = b.pid ? s.heroes.find((x) => x.pid === b.pid) : undefined;
    if (b.pid && (!h || h.downTicks > 0)) {
      // Its carrier fell: it drops where they were.
      b.pid = '';
      b.held = 0;
    } else if (h) {
      b.x = h.x;
      b.y = h.y;
      // At an open crack: planted, and lit.
      const i = r.cracks.findIndex((c, k) => !st.sealed[k] && withinRange(c.x, c.y, h.x, h.y, r.plant));
      if (i >= 0) {
        const c = r.cracks[i] as { x: number; y: number };
        b.pid = '';
        b.crack = i;
        b.x = c.x;
        b.y = c.y;
        b.fuse = s.tick + r.fuse;
        ev(s, { t: 'blast', kind: 'plant', x: c.x, y: c.y });
      }
    } else {
      // On the ground: a hero with free hands who stays by it lifts it.
      const by = standing.find((x) => !carried(s, x) && withinRange(x.x, x.y, b.x, b.y, r.reach));
      b.held = by ? b.held + 1 : 0;
      if (by && b.held >= r.pick) {
        b.pid = by.pid;
        ev(s, { t: 'blast', kind: 'lift', x: b.x, y: b.y });
      }
    }
    keep.push(b);
  }
  st.barrels = keep;
  // Every crack sealed: the beacon is lit.
  if (st.sealed.every(Boolean)) {
    st.lit = s.tick;
    st.barrels = [];
    ev(s, { t: 'blast', kind: 'beacon', x: 0, y: 0 });
  }
};

/** A barrel goes off: everything near it is hurt; at a crack, the crack is sealed. */
const boom = (s: BattleState, b: BlastBarrel, planted: boolean): void => {
  const r = s.rules?.blast;
  const st = s.blast;
  if (!r || !st) return;
  for (const e of s.enemies) if (e.hp > 0 && withinRange(e.x, e.y, b.x, b.y, r.radius)) damageEnemy(s, e, r.foe, { pid: '', ignoresArmor: true, noReact: true });
  for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, b.x, b.y, r.radius)) damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp));
  if (planted && b.crack >= 0) {
    // A crack takes `charges` blasts to seal; each one before that only cracks it shut a little more.
    st.blasts[b.crack] = (st.blasts[b.crack] ?? 0) + 1;
    if ((st.blasts[b.crack] ?? 0) >= r.charges) st.sealed[b.crack] = true;
    ev(s, { t: 'blast', kind: st.sealed[b.crack] ? 'seal' : 'crack', x: b.x, y: b.y });
  } else ev(s, { t: 'blast', kind: 'burst', x: b.x, y: b.y });
};

/** A blow on a hero carrying a barrel lands on the barrel too. */
export const blastHit = (s: BattleState, h: Hero, amount: number): void => {
  const b = s.blast ? carried(s, h) : undefined;
  if (b) b.hp -= amount;
};

/** Whether a spawn's crack is sealed: nothing comes out of it any more. */
export const crackSealed = (s: BattleState, crack: number | undefined): boolean => crack !== undefined && !!s.blast?.sealed[crack];

/** Lit: won; the tower falling: lost. */
export const blastResult = (s: BattleState): 'won' | 'lost' | null => {
  if (!s.rules?.blast) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.blast?.lit && s.tick - s.blast.lit >= s.rules.blast.beat ? 'won' : null;
};
